import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Avatar,
    Badge,
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import { useApp } from '../../context/AppContext';
import server from '../../global/server';
import useHandleErrors from '../../hooks/useHandleErrors';

const hora = valor =>
    new Date(valor).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const dia = valor =>
    new Date(valor).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });

/**
 * Fila de atendimento do backoffice.
 *
 * A lista da esquerda é a fila; a direita, a conversa. O admin só consegue
 * escrever depois de clicar em "Iniciar atendimento" — antes disso a
 * solicitação não é de ninguém, e duas pessoas responderiam por cima uma da
 * outra.
 *
 * Tudo em tempo real: solicitação nova, mensagem nova e mudança de status
 * chegam por socket, sem recarregar a página.
 */
const SupportQueue = () => {
    const { socket } = useApp();
    const [aba, setAba] = useState('fila');
    const [fila, setFila] = useState([]);
    const [historico, setHistorico] = useState([]);
    const [busca, setBusca] = useState('');
    const [aberto, setAberto] = useState(null);
    const [mensagens, setMensagens] = useState([]);
    const [texto, setTexto] = useState('');
    const [erro, setErro] = useState();
    useHandleErrors(erro);

    const fimDaLista = useRef(null);
    // Ler o id atual dentro do handler do socket sem reassinar o listener a
    // cada troca de conversa
    const abertoRef = useRef(null);
    abertoRef.current = aberto?.id ?? null;
    const abaRef = useRef('fila');
    abaRef.current = aba;
    const buscaRef = useRef('');
    buscaRef.current = busca;

    const carregarFila = useCallback(async () => {
        try {
            const { data } = await server.get('/backoffice/suporte');
            setFila(data.data);
        } catch (falha) {
            setErro(falha);
        }
    }, []);

    const abrir = useCallback(async ticket => {
        try {
            const { data } = await server.get(`/backoffice/suporte/${ticket.id}`);
            setAberto(data.data);
            setMensagens(data.data.messages || []);
            carregarFila();
        } catch (falha) {
            setErro(falha);
        }
    }, [carregarFila]);

    const carregarHistorico = useCallback(async termo => {
        try {
            const { data } = await server.get('/backoffice/suporte/historico', {
                params: termo ? { busca: termo } : {},
            });
            setHistorico(data.data);
        } catch (falha) {
            setErro(falha);
        }
    }, []);

    useEffect(() => {
        carregarFila();
    }, [carregarFila]);

    useEffect(() => {
        if (aba === 'historico') carregarHistorico(busca);
        // A busca so dispara pelo botao/Enter: recarregar a cada tecla faria
        // uma consulta por letra digitada
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aba]);

    useEffect(() => {
        fimDaLista.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mensagens]);

    useEffect(() => {
        if (!socket) return;

        const aoChegarSolicitacao = () => carregarFila();

        const aoChegarMensagem = ({ ticket, ticket_id, message }) => {
            const alvo = ticket?.id ?? ticket_id;
            if (alvo === abertoRef.current) {
                setMensagens(atual =>
                    atual.some(item => item.id === message.id) ? atual : [...atual, message],
                );
            }
            carregarFila();
        };

        const aoAtualizar = ({ ticket }) => {
            setAberto(atual => (atual && atual.id === ticket.id ? { ...atual, ...ticket } : atual));
            carregarFila();
            // Encerrar tira da fila e poe no historico: se ele estiver aberto,
            // precisa refletir na hora
            if (abaRef.current === 'historico') carregarHistorico(buscaRef.current);
        };

        socket.on('suporte:nova-solicitacao', aoChegarSolicitacao);
        socket.on('suporte:mensagem', aoChegarMensagem);
        socket.on('suporte:atualizado', aoAtualizar);

        return () => {
            socket.off('suporte:nova-solicitacao', aoChegarSolicitacao);
            socket.off('suporte:mensagem', aoChegarMensagem);
            socket.off('suporte:atualizado', aoAtualizar);
        };
    }, [socket, carregarFila, carregarHistorico]);

    const iniciar = async () => {
        try {
            const { data } = await server.post(`/backoffice/suporte/${aberto.id}/iniciar`);
            setAberto(atual => ({ ...atual, ...data.data }));
            carregarFila();
        } catch (falha) {
            setErro(falha);
        }
    };

    const encerrar = async () => {
        try {
            const { data } = await server.post(`/backoffice/suporte/${aberto.id}/encerrar`);
            setAberto(atual => ({ ...atual, ...data.data }));
            carregarFila();
        } catch (falha) {
            setErro(falha);
        }
    };

    const enviar = async () => {
        const corpo = texto.trim();
        if (!corpo) return;

        try {
            const { data } = await server.post(`/backoffice/suporte/${aberto.id}/mensagem`, { body: corpo });
            setTexto('');
            setMensagens(atual =>
                atual.some(item => item.id === data.data.id) ? atual : [...atual, data.data],
            );
            carregarFila();
        } catch (falha) {
            setErro(falha);
        }
    };

    const emAndamento = aberto?.status === 'EM_ANDAMENTO';

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Fila de atendimento
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Tabs
                        value={aba}
                        onChange={(_, valor) => setAba(valor)}
                        sx={{
                            mb: 1,
                            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                            '& .Mui-selected': { color: '#006a61' },
                            '& .MuiTabs-indicator': { backgroundColor: '#006a61' },
                        }}
                    >
                        <Tab value="fila" label={`Fila (${fila.length})`} />
                        <Tab value="historico" label="Histórico" />
                    </Tabs>

                    {aba === 'historico' && (
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Buscar por nome ou usuário"
                                value={busca}
                                onChange={evento => setBusca(evento.target.value)}
                                onKeyDown={evento => {
                                    if (evento.key === 'Enter') carregarHistorico(busca);
                                }}
                            />
                            <Button
                                variant="outlined"
                                onClick={() => carregarHistorico(busca)}
                                sx={{ textTransform: 'none', borderColor: '#006a61', color: '#006a61' }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}

                    <Paper variant="outlined" sx={{ borderRadius: '12px', maxHeight: '62vh', overflowY: 'auto' }}>
                        {aba === 'historico' ? (
                            historico.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                                    Nenhum atendimento encerrado.
                                </Typography>
                            ) : (
                                historico.map(ticket => (
                                    <Box key={ticket.id}>
                                        <Stack
                                            direction="row"
                                            spacing={1.5}
                                            alignItems="center"
                                            onClick={() => abrir(ticket)}
                                            sx={{
                                                p: 1.5,
                                                cursor: 'pointer',
                                                bgcolor: aberto?.id === ticket.id ? '#f2f9f8' : 'transparent',
                                                '&:hover': { bgcolor: '#f7fbfa' },
                                            }}
                                        >
                                            <Avatar src={ticket.user?.profileImage} sx={{ width: 40, height: 40 }} />
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Typography variant="subtitle2" noWrap>
                                                    {ticket.user?.name}
                                                    <Typography component="span" variant="caption" color="text.disabled">
                                                        {' '}#{ticket.id}
                                                    </Typography>
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                    {ticket._count?.messages} mensagem(ns) ·{' '}
                                                    {ticket.admin?.name || 'sem atendente'}
                                                </Typography>
                                            </Box>
                                            <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                                                {ticket.closed_at ? dia(ticket.closed_at) : ''}
                                            </Typography>
                                        </Stack>
                                        <Divider />
                                    </Box>
                                ))
                            )
                        ) : fila.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                                Nenhuma solicitação em aberto.
                            </Typography>
                        ) : (
                            fila.map(ticket => (
                                <Box key={ticket.id}>
                                    <Stack
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="center"
                                        onClick={() => abrir(ticket)}
                                        sx={{
                                            p: 1.5,
                                            cursor: 'pointer',
                                            bgcolor: aberto?.id === ticket.id ? '#f2f9f8' : 'transparent',
                                            '&:hover': { bgcolor: '#f7fbfa' },
                                        }}
                                    >
                                        <Badge badgeContent={ticket._count?.messages || 0} color="error">
                                            <Avatar src={ticket.user?.profileImage} sx={{ width: 40, height: 40 }} />
                                        </Badge>

                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Stack direction="row" alignItems="baseline" spacing={0.75}>
                                                <Typography variant="subtitle2" noWrap>
                                                    {ticket.user?.name}
                                                </Typography>
                                                {/* Numero e hora porque a mesma
                                                    pessoa pode ter mais de uma
                                                    solicitacao aberta: sem eles
                                                    as duas linhas ficam iguais */}
                                                <Typography variant="caption" color="text.disabled">
                                                    #{ticket.id} · {hora(ticket.createdAt)}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                {ticket.lastMessage}
                                            </Typography>
                                        </Box>

                                        <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                                            {ticket.kind === 'DENUNCIA' && (
                                                <Chip size="small" color="error" label="Denúncia" />
                                            )}
                                            <Chip
                                                size="small"
                                                label={ticket.status === 'AGUARDANDO' ? 'Na fila' : 'Em atendimento'}
                                                color={ticket.status === 'AGUARDANDO' ? 'warning' : 'success'}
                                            />
                                        </Stack>
                                    </Stack>
                                    <Divider />
                                </Box>
                            ))
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    {!aberto ? (
                        <Paper
                            variant="outlined"
                            sx={{
                                borderRadius: '12px',
                                height: '70vh',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                            }}
                        >
                            <HeadsetMicIcon sx={{ fontSize: 48, color: '#bdbdbd' }} />
                            <Typography variant="body2" color="text.secondary">
                                Escolha uma solicitação da fila.
                            </Typography>
                        </Paper>
                    ) : (
                        <Paper variant="outlined" sx={{ borderRadius: '12px', height: '70vh', display: 'flex', flexDirection: 'column' }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ p: 1.5 }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Avatar src={aberto.user?.profileImage} />
                                    <Box>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="subtitle2">{aberto.user?.name}</Typography>
                                            {aberto.kind === 'DENUNCIA' && (
                                                <Chip size="small" color="error" label="Denúncia" />
                                            )}
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary">
                                            @{aberto.user?.username}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    {aberto.status === 'AGUARDANDO' && (
                                        <Button
                                            variant="contained"
                                            onClick={iniciar}
                                            sx={{ bgcolor: '#006a61', textTransform: 'none', '&:hover': { bgcolor: '#005a52' } }}
                                        >
                                            Iniciar atendimento
                                        </Button>
                                    )}
                                    {emAndamento && (
                                        <Button variant="outlined" color="error" onClick={encerrar} sx={{ textTransform: 'none' }}>
                                            Encerrar
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>

                            <Divider />

                            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: '#fafafa' }}>
                                <Stack spacing={1}>
                                    {mensagens.map(mensagem => {
                                        // Mesmo aviso que o usuario ve: faixa
                                        // inteira, marcando o fim do atendimento
                                        if (mensagem.sender === 'SISTEMA') {
                                            return (
                                                <Box
                                                    key={mensagem.id}
                                                    sx={{
                                                        alignSelf: 'stretch',
                                                        width: '100%',
                                                        bgcolor: '#fff8e1',
                                                        border: '1px solid #ffe0b2',
                                                        borderRadius: '10px',
                                                        px: 2,
                                                        py: 1.5,
                                                        my: 1,
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ color: '#8a5a00', lineHeight: 1.5 }}>
                                                        {mensagem.body}
                                                    </Typography>
                                                </Box>
                                            );
                                        }

                                        const doAdmin = mensagem.sender === 'ADMIN';
                                        return (
                                            <Box
                                                key={mensagem.id}
                                                sx={{
                                                    alignSelf: doAdmin ? 'flex-end' : 'flex-start',
                                                    maxWidth: '75%',
                                                    bgcolor: doAdmin ? '#006a61' : 'white',
                                                    color: doAdmin ? 'white' : 'text.primary',
                                                    border: doAdmin ? 'none' : '1px solid #e8e8e8',
                                                    borderRadius: '12px',
                                                    px: 1.5,
                                                    py: 1,
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {mensagem.body}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ opacity: 0.7, display: 'block', textAlign: 'right' }}
                                                >
                                                    {hora(mensagem.createdAt)}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                    <div ref={fimDaLista} />
                                </Stack>
                            </Box>

                            <Divider />

                            {aberto.status === 'ENCERRADO' ? (
                                <Alert severity="info" sx={{ borderRadius: 0 }}>
                                    Atendimento encerrado.
                                </Alert>
                            ) : !emAndamento ? (
                                <Alert severity="warning" sx={{ borderRadius: 0 }}>
                                    Clique em "Iniciar atendimento" para poder responder.
                                </Alert>
                            ) : (
                                <Stack direction="row" spacing={1} sx={{ p: 1.5 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        multiline
                                        maxRows={4}
                                        placeholder="Escreva a resposta"
                                        value={texto}
                                        onChange={evento => setTexto(evento.target.value)}
                                        onKeyDown={evento => {
                                            if (evento.key === 'Enter' && !evento.shiftKey) {
                                                evento.preventDefault();
                                                enviar();
                                            }
                                        }}
                                    />
                                    <IconButton
                                        onClick={enviar}
                                        disabled={!texto.trim()}
                                        sx={{ bgcolor: '#006a61', color: 'white', '&:hover': { bgcolor: '#005a52' } }}
                                    >
                                        <SendIcon />
                                    </IconButton>
                                </Stack>
                            )}
                        </Paper>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default SupportQueue;
